import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDomainStore, useFamilyStore, useFamilyStoriesStore } from '@/hooks/useStores';
import StoryForm, { type StoryFormValues } from '@/components/person/StoryForm';
import FamilyMemoryCard from '@/components/memories/FamilyMemoryCard';
import StoryMediaThumb from '@/components/memories/StoryMediaThumb';
import Spinner from '@/components/shared/Spinner';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import { mediaApi } from '@/api/mediaWrapper';
import { canModifyFamily } from '@/utils/rolePermissions';
import {
  canEditOrDeleteStories,
  canEditOrDeleteStory,
  formatStoryDate,
  storyReadMinutes,
  storyWordCount
} from '@/types/story';
import ChevronRight from '@/icons/ChevronRight';
import ManageContentPermissionsModal from '@/components/modals/ManageContentPermissionsModal';
import { ContentType, type MediaAssetResponse } from 'we-tree-api-client/v2';

import './memory-detail-screen.css';

function copyMemoryLink(familyId: string, storyId: string) {
  const url = `${window.location.origin}/families/${familyId}/memories/${storyId}`;
  void navigator.clipboard.writeText(url);
}

const MemoryDetailScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const familyStoriesStore = useFamilyStoriesStore();
  const navigate = useNavigate();
  const { familyId: familyIdParam, storyId: storyIdParam } = useParams<{ familyId: string; storyId: string }>();

  // Read the live MST node from the store — never cache it in React state. Replacing the story
  // in `items` (e.g. Strict Mode double-fetch on a deep link) detaches the old node; holding that
  // reference causes "no longer part of a state tree" errors while the screen still loads.
  const story = storyIdParam ? familyStoriesStore.getStoryById(storyIdParam) : undefined;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [memberPhotoUrl, setMemberPhotoUrl] = useState<string | null>(null);
  const [viewerAssetId, setViewerAssetId] = useState<string | null>(null);
  const [showContentPermissions, setShowContentPermissions] = useState(false);
  const [permissionsAsset, setPermissionsAsset] = useState<MediaAssetResponse | null>(null);

  const familyRole = domainStore.currentFamily?.userRole ?? null;
  const canEditDelete = canEditOrDeleteStory(story, canEditOrDeleteStories(canModifyFamily(familyRole), familyRole));
  const canManagePermissions = story?.canManageAccess === true;
  // Anyone with more than view access to this memory relates to the member it is about, so they may
  // add another about the same person even when their family role would not allow it.
  const canAddRelatedMemory =
    canModifyFamily(familyRole) || (story?.effectiveRole != null && story.effectiveRole !== 'viewer');

  useEffect(() => {
    if (!familyIdParam) return;
    domainStore.setSelectedFamily(familyIdParam);
    if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
      void familyStore.loadFamilyData(familyIdParam);
    }
  }, [domainStore, familyStore, familyIdParam]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!familyIdParam || !storyIdParam) return;
      setIsLoading(true);
      setError(null);
      setMemberPhotoUrl(null);
      const result = await familyStoriesStore.loadStoryDetail(familyIdParam, storyIdParam);
      if (cancelled) return;

      if (result.success && result.data) {
        const memberId = result.data.familyMemberId ?? null;
        if (memberId) {
          void familyStoriesStore.loadRelatedStories(familyIdParam, memberId, storyIdParam);
          const photoResult = await mediaApi.getPrimaryProfilePhotoDisplay(familyIdParam, memberId, '200x200');
          if (cancelled) return;
          if (photoResult.success && photoResult.data?.url) {
            setMemberPhotoUrl(photoResult.data.url);
          } else {
            setMemberPhotoUrl(null);
          }
        }
      } else {
        setError(result.error?.getUserMessage?.() || result.error?.message || t('memoryDetail.notFound'));
      }
      setIsLoading(false);
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [familyIdParam, storyIdParam, familyStoriesStore, t]);

  const memberId = story?.familyMemberId;
  const person = memberId ? familyStore.getPerson(memberId) : undefined;
  const memberName =
    person?.fullName || (person ? `${person.firstName} ${person.lastName}`.trim() : tForms('person.memberFallback'));
  const firstName = person?.firstName?.trim() || memberName.split(' ')[0] || t('memories.them');

  const handleUpdate = async (values: StoryFormValues) => {
    if (!familyIdParam || !storyIdParam) return;
    setIsSaving(true);
    setFormError(null);
    const result = await familyStoriesStore.updateStory(familyIdParam, storyIdParam, {
      title: values.title,
      content: values.content,
      mediaAssetIds: values.mediaAssetIds
    });
    setIsSaving(false);
    if (result.success) {
      await familyStoriesStore.loadStoryDetail(familyIdParam, storyIdParam);
      setIsEditing(false);
    } else {
      setFormError(result.error?.getUserMessage?.() || result.error?.message || t('memoryDetail.saveFailed'));
    }
  };

  const handleDelete = async () => {
    if (!familyIdParam || !storyIdParam) return;
    if (!window.confirm(tForms('confirm.deleteMemory'))) return;
    const result = await familyStoriesStore.deleteStory(familyIdParam, storyIdParam);
    if (result.success) {
      navigate(`/families/${familyIdParam}/memories`);
    } else {
      setError(result.error?.getUserMessage?.() || result.error?.message || t('memoryDetail.deleteFailed'));
    }
  };

  const viewerMimeType = story?.mediaAttachments.find((a) => a.mediaAssetId === viewerAssetId)?.mimeType ?? null;

  if (isLoading) {
    return (
      <div className="memory-detail-screen">
        <Spinner message={t('memoryDetail.loading')} />
      </div>
    );
  }

  if (error || !story || !familyIdParam) {
    return (
      <div className="memory-detail-screen">
        <div className="memory-detail-screen__error" role="alert">
          {error || t('memoryDetail.notFound')}
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => navigate(`/families/${familyIdParam}/memories`)}
        >
          {tForms('actions.allMemories')}
        </button>
      </div>
    );
  }

  return (
    <div className="memory-detail-screen">
      <div className="memory-detail-screen__back-row">
        <button
          type="button"
          className="memory-detail-screen__back"
          onClick={() => navigate(`/families/${familyIdParam}/memories`)}
        >
          {tForms('actions.allMemories')}
        </button>
        <div className="memory-detail-screen__actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => copyMemoryLink(familyIdParam, story.id)}
          >
            {tForms('actions.copyLink')}
          </button>
          {canManagePermissions && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowContentPermissions(true)}>
              {tForms('actions.permissions')}
            </button>
          )}
          {canEditDelete && (
            <>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setIsEditing(true)}>
                {tCommon('edit')}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm memory-detail-screen__delete"
                onClick={handleDelete}
              >
                {tCommon('delete')}
              </button>
            </>
          )}
        </div>
      </div>

      <p className="memory-detail-screen__lede">
        {t('memoryDetail.lede')} <ScreenHelpDot />
      </p>

      {memberId && (
        <button
          type="button"
          className="memory-detail-screen__about"
          onClick={() => navigate(`/families/${familyIdParam}/people/${memberId}?section=memories`)}
        >
          <div className="memory-detail-screen__about-photo">
            {memberPhotoUrl ? <img src={memberPhotoUrl} alt="" /> : <span>{memberName.charAt(0).toUpperCase()}</span>}
          </div>
          <div className="memory-detail-screen__about-text">
            <span className="memory-detail-screen__about-label">{t('memories.about')}</span>
            <span className="memory-detail-screen__about-name">{memberName}</span>
          </div>
          <ChevronRight width={16} height={16} />
        </button>
      )}

      {isEditing ? (
        <StoryForm
          familyId={familyIdParam}
          familyMemberId={memberId!}
          isEditing
          isSaving={isSaving}
          errorMessage={formError}
          initialValues={{
            title: story.title,
            content: story.content,
            mediaAssetIds: story.mediaAttachments.map((a) => a.mediaAssetId),
            mediaMeta: Object.fromEntries(
              story.mediaAttachments.map((a) => [
                a.mediaAssetId,
                { mimeType: a.mimeType, filename: a.originalFilename }
              ])
            )
          }}
          onCancel={() => {
            setIsEditing(false);
            setFormError(null);
          }}
          onSubmit={handleUpdate}
        />
      ) : (
        <article className="memory-detail-screen__article">
          <h1 className="memory-detail-screen__title">{story.title}</h1>
          <div className="memory-detail-screen__meta">
            <span>{story.authorLabel}</span>
            <span>·</span>
            <span>{formatStoryDate(story.createdAt)}</span>
            <span>·</span>
            <span>
              {t('memoryDetail.readMeta', {
                words: storyWordCount(story.content),
                minutes: storyReadMinutes(story.content)
              })}
            </span>
          </div>
          <div className="memory-detail-screen__body">{story.content}</div>
        </article>
      )}

      {story.mediaAttachments.length > 0 && !isEditing && (
        <section className="memory-detail-screen__attachments">
          <h2 className="memory-detail-screen__section-title">{t('memoryDetail.attachments')}</h2>
          <div className="memory-detail-screen__attachment-list">
            {story.mediaAttachments.map((attachment) => {
              const mime = (attachment.mimeType || '').toLowerCase();
              const openVerb = mime.startsWith('video/')
                ? t('memoryDetail.playVideo')
                : mime.startsWith('audio/')
                  ? t('memoryDetail.playAudio')
                  : t('memoryDetail.viewPhoto');
              const openLabel = t('memoryDetail.openAttachment', {
                verb: openVerb,
                filename: attachment.originalFilename
              });
              return (
                <StoryMediaThumb
                  key={attachment.id}
                  familyId={familyIdParam}
                  mediaAssetId={attachment.mediaAssetId}
                  mimeType={attachment.mimeType}
                  size="40x40"
                  onClick={() => setViewerAssetId(attachment.mediaAssetId)}
                  label={openLabel}
                />
              );
            })}
          </div>
        </section>
      )}

      {familyIdParam && viewerAssetId && (
        <ImageEnlargeOverlay
          isOpen={Boolean(viewerAssetId)}
          onClose={() => setViewerAssetId(null)}
          familyId={familyIdParam}
          mediaAssetId={viewerAssetId}
          mimeType={viewerMimeType}
          onManagePermissions={(asset) => {
            setViewerAssetId(null);
            setPermissionsAsset(asset);
          }}
        />
      )}

      {familyIdParam && permissionsAsset && (
        <ManageContentPermissionsModal
          familyId={familyIdParam}
          contentType={ContentType.MediaAsset}
          contentId={permissionsAsset.id}
          contentLabel={
            permissionsAsset.originalFilename?.trim() ||
            tForms('contentAccess.thisItem', { type: tForms('contentAccess.typeMedia') })
          }
          canManage={permissionsAsset.canManageAccess === true}
          actorEffectiveRole={permissionsAsset.effectiveRole ?? null}
          isOpen={Boolean(permissionsAsset)}
          onClose={() => setPermissionsAsset(null)}
        />
      )}

      {familyIdParam && story && (
        <ManageContentPermissionsModal
          familyId={familyIdParam}
          contentType={ContentType.Story}
          contentId={story.id}
          contentLabel={
            story.title?.trim() || tForms('contentAccess.thisItem', { type: tForms('contentAccess.typeMemory') })
          }
          canManage={canManagePermissions}
          actorEffectiveRole={story.effectiveRole}
          isOpen={showContentPermissions}
          onClose={() => setShowContentPermissions(false)}
        />
      )}

      {memberId && familyStoriesStore.relatedStories.length > 0 && !isEditing && (
        <section className="memory-detail-screen__related">
          <h2 className="memory-detail-screen__section-title">{t('memoryDetail.moreOf', { name: firstName })}</h2>
          <div className="memory-detail-screen__related-grid">
            {familyStoriesStore.relatedStories.map((related) => (
              <FamilyMemoryCard key={related.id} story={related} familyId={familyIdParam} compact />
            ))}
          </div>
          {canAddRelatedMemory && (
            <button
              type="button"
              className="memory-detail-screen__add-related"
              onClick={() => navigate(`/families/${familyIdParam}/people/${memberId}?section=memories`)}
            >
              {t('memoryDetail.addMemoryPlus')}
            </button>
          )}
        </section>
      )}
    </div>
  );
});

export default MemoryDetailScreen;
